import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import apiClient from '@/lib/api/client'
import type { ApiResponse } from '@/lib/api/types'
import { toast } from '@/components/ui/Toast'
import { useAuthStore } from '@/stores/authStore'

export interface ReviewItem {
  id: number
  userId: number
  userName: string
  facilityId: number
  rating: number
  content: string
  visitDate: string | null
  isMine: boolean
  createdAt: string
  // 시설관리자 답변 (Step 6-A)
  replyContent: string | null
  replyByName: string | null
  repliedAt: string | null
}

export interface ReviewSummary {
  totalCount: number
  averageRating: number
}

interface ReviewPageResponse {
  success: boolean
  data: {
    content: ReviewItem[]
    totalElements: number
    totalPages: number
    number: number
    last: boolean
  }
}

export function useReviewSummary(facilityId: number) {
  return useQuery({
    queryKey: ['reviewSummary', facilityId],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<ReviewSummary>>('/reviews/summary', {
        params: { facilityId },
      })
      return res.data.data
    },
    enabled: facilityId > 0,
  })
}

export function useReviews(facilityId: number) {
  const userId = useAuthStore((s) => s.user?.id ?? null)
  return useInfiniteQuery({
    queryKey: ['reviews', facilityId, userId],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await apiClient.get<ReviewPageResponse>('/reviews', {
        params: { facilityId, page: pageParam, size: 10 },
      })
      return res.data
    },
    getNextPageParam: (lastPage) =>
      lastPage?.data && !lastPage.data.last ? lastPage.data.number + 1 : undefined,
    initialPageParam: 0,
    enabled: facilityId > 0,
  })
}

export function useCreateReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { facilityId: number; rating: number; content: string; visitDate?: string }) => {
      const res = await apiClient.post<ApiResponse<ReviewItem>>('/reviews', data)
      return res.data.data
    },
    onSuccess: (_, vars) => {
      toast.success('후기가 등록되었습니다')
      qc.invalidateQueries({ queryKey: ['reviews', vars.facilityId] })
      qc.invalidateQueries({ queryKey: ['reviewSummary', vars.facilityId] })
    },
    onError: () => toast.error('후기 등록에 실패했습니다'),
  })
}

export function useDeleteReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ reviewId, facilityId }: { reviewId: number; facilityId: number }) => {
      await apiClient.delete(`/reviews/${reviewId}`)
      return facilityId
    },
    onSuccess: (facilityId) => {
      toast.success('후기가 삭제되었습니다')
      qc.invalidateQueries({ queryKey: ['reviews', facilityId] })
      qc.invalidateQueries({ queryKey: ['reviewSummary', facilityId] })
    },
    onError: () => toast.error('삭제에 실패했습니다'),
  })
}

// ═══════════════════════════════════════════════════════════════
// 시설관리자 리뷰 답변 (Step 6-A)
// ═══════════════════════════════════════════════════════════════

export function useFacilityReviews() {
  return useInfiniteQuery({
    queryKey: ['facility-reviews'],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await apiClient.get<ReviewPageResponse>('/facility-manager/reviews', {
        params: { page: pageParam, size: 20 },
      })
      return res.data
    },
    getNextPageParam: (lastPage) =>
      lastPage?.data && !lastPage.data.last ? lastPage.data.number + 1 : undefined,
    initialPageParam: 0,
  })
}

export function useReplyToReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ reviewId, content }: { reviewId: number; content: string }) => {
      await apiClient.post(`/facility-manager/reviews/${reviewId}/reply`, { content })
    },
    onSuccess: () => {
      toast.success('답변이 등록되었어요')
      qc.invalidateQueries({ queryKey: ['facility-reviews'] })
      qc.invalidateQueries({ queryKey: ['reviews'] })
    },
    onError: () => toast.error('답변 등록에 실패했어요'),
  })
}

export function useUpdateReply() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ reviewId, content }: { reviewId: number; content: string }) => {
      await apiClient.patch(`/facility-manager/reviews/${reviewId}/reply`, { content })
    },
    onSuccess: () => {
      toast.success('답변이 수정되었어요')
      qc.invalidateQueries({ queryKey: ['facility-reviews'] })
      qc.invalidateQueries({ queryKey: ['reviews'] })
    },
    onError: () => toast.error('답변 수정에 실패했어요'),
  })
}

export function useDeleteReply() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (reviewId: number) => {
      await apiClient.delete(`/facility-manager/reviews/${reviewId}/reply`)
    },
    onSuccess: () => {
      toast.success('답변이 삭제되었어요')
      qc.invalidateQueries({ queryKey: ['facility-reviews'] })
      qc.invalidateQueries({ queryKey: ['reviews'] })
    },
    onError: () => toast.error('답변 삭제에 실패했어요'),
  })
}
